import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUpsertInventory, useInventoryImages } from '../../hooks/useInventory';
import { useAuthStore } from '../../stores/auth.store';
import { uploadApi } from '../../api/endpoints';
import type { Inventory } from '@motra/shared';

const ITEM_STATUSES = ['active', 'inactive', 'discontinued'];

export default function InventoryForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEdit = Boolean(id);

  const shop = useAuthStore((s) => s.shop);
  const account = useAuthStore((s) => s.account);

  // Parse categories
  let categories: string[] = [];
  try {
    categories = account?.inv_type ? JSON.parse(account.inv_type) : [];
  } catch { /* empty */ }

  const upsert = useUpsertInventory();
  const { data: images = [] } = useInventoryImages(id);

  const [form, setForm] = useState<Partial<Inventory>>({
    status: 'active',
    qty: 0,
    price: 0,
    cost: 0,
  });
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);

  function setField<K extends keyof Inventory>(key: K, value: Inventory[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!form.name?.trim()) {
      setError('Name is required.');
      return;
    }

    try {
      await upsert.mutateAsync({
        ...form,
        ...(id ? { id } : {}),
        shopid: shop?.id,
      });
      navigate('/inventory');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    }
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('ref_type', 'inventory');
      if (id) fd.append('ref_id', id);
      await uploadApi.uploadFile(fd);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">
          {isEdit ? t('edit', 'Edit Item') : `${t('add', 'Add')} Item`}
        </h1>
        <button className="btn btn--outline" onClick={() => navigate('/inventory')}>
          {t('back', 'Back')}
        </button>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">{t('name', 'Name')} *</label>
            <input
              type="text"
              className="form-input"
              value={form.name ?? ''}
              onChange={(e) => setField('name', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('category', 'Category')}</label>
            <select
              className="form-select"
              value={form.category ?? ''}
              onChange={(e) => setField('category', e.target.value)}
            >
              <option value="">— Select —</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="form-group form-group--full">
            <label className="form-label">{t('description', 'Description')}</label>
            <textarea
              className="form-textarea"
              value={form.description ?? ''}
              onChange={(e) => setField('description', e.target.value)}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('quantity', 'Quantity')}</label>
            <input
              type="number"
              className="form-input"
              value={form.qty ?? 0}
              onChange={(e) => setField('qty', parseFloat(e.target.value) || 0)}
              step="1"
              min={0}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('price', 'Price')}</label>
            <input
              type="number"
              className="form-input"
              value={form.price ?? 0}
              onChange={(e) => setField('price', parseFloat(e.target.value) || 0)}
              step="0.01"
              min={0}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('cost', 'Cost')}</label>
            <input
              type="number"
              className="form-input"
              value={form.cost ?? 0}
              onChange={(e) => setField('cost', parseFloat(e.target.value) || 0)}
              step="0.01"
              min={0}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Promo Price</label>
            <input
              type="number"
              className="form-input"
              value={form.pprice ?? ''}
              onChange={(e) =>
                setField('pprice', e.target.value ? parseFloat(e.target.value) : undefined)
              }
              step="0.01"
              min={0}
              placeholder="Leave blank if no promo"
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('barcode', 'Barcode')}</label>
            <input
              type="text"
              className="form-input"
              value={form.barcode ?? ''}
              onChange={(e) => setField('barcode', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('supplier', 'Supplier')}</label>
            <input
              type="text"
              className="form-input"
              value={form.supplier ?? ''}
              onChange={(e) => setField('supplier', e.target.value)}
              placeholder="Supplier ID or name"
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('status', 'Status')}</label>
            <select
              className="form-select"
              value={form.status ?? 'active'}
              onChange={(e) => setField('status', e.target.value)}
            >
              {ITEM_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Image upload */}
        {isEdit && (
          <div className="form-section">
            <h3 className="form-section-title">Images</h3>
            <div className="image-slots">
              {images.map((img) => (
                <div key={img.id} className="image-slot">
                  <img src={img.url} alt="product" className="image-slot__preview" />
                </div>
              ))}
              <label className="image-slot image-slot--upload">
                <input
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
                {uploading ? '...' : '+ Add Image'}
              </label>
            </div>
          </div>
        )}

        {error && <div className="alert alert--error">{error}</div>}

        <div className="form-actions">
          <button
            type="button"
            className="btn btn--outline"
            onClick={() => navigate('/inventory')}
          >
            {t('cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={upsert.isPending}
          >
            {upsert.isPending ? 'Saving...' : t('save', 'Save')}
          </button>
        </div>
      </form>
    </div>
  );
}
